import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Target, DollarSign, TrendingUp, TrendingDown, Calendar,
  Shield, AlertTriangle, CheckCircle2, BarChart3, ArrowRight,
  Clock, Percent, Scale, Layers, RefreshCw, Zap, Calculator, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const COMMITMENT_DEMO_DATA = {
  summary: {
    totalCommitments: 2340000,
    currentCoverage: 62,
    optimalCoverage: 78,
    annualSavingsGap: 89400,
    riUtilization: 84,
    spUtilization: 71,
    expiringNext30d: 3,
    overCommitted: 145000
  },
  currentCommitments: [
    { id: 'c1', type: 'Reserved Instance', cloud: 'AWS', service: 'EC2 (m5.2xlarge)', term: '3-year', monthlyCommit: 18400, utilization: 92, savings: 38, expiry: '2027-03-15', status: 'healthy' },
    { id: 'c2', type: 'Savings Plan', cloud: 'AWS', service: 'Compute', term: '1-year', monthlyCommit: 42000, utilization: 71, savings: 24, expiry: '2026-08-20', status: 'warning' },
    { id: 'c3', type: 'Reserved Instance', cloud: 'Azure', service: 'D-series VMs', term: '3-year', monthlyCommit: 12800, utilization: 88, savings: 42, expiry: '2027-11-01', status: 'healthy' },
    { id: 'c4', type: 'Committed Use', cloud: 'GCP', service: 'N2 Machine Type', term: '3-year', monthlyCommit: 8900, utilization: 95, savings: 57, expiry: '2028-01-10', status: 'optimal' },
    { id: 'c5', type: 'Savings Plan', cloud: 'AWS', service: 'SageMaker', term: '1-year', monthlyCommit: 5400, utilization: 43, savings: 8, expiry: '2026-05-30', status: 'critical' },
    { id: 'c6', type: 'Reserved Capacity', cloud: 'Azure', service: 'Cosmos DB (400 RUs)', term: '1-year', monthlyCommit: 3200, utilization: 67, savings: 31, expiry: '2026-06-15', status: 'warning' },
  ],
  recommendations: [
    { action: 'Convert to 3-year RI', target: 'EC2 c5.4xlarge (12 instances)', cloud: 'AWS', currentCost: 8640, projectedCost: 4320, savings: 4320, confidence: 94, risk: 'low', breakEven: '4.2 months' },
    { action: 'Add Compute Savings Plan', target: 'Lambda + Fargate workloads', cloud: 'AWS', currentCost: 12400, projectedCost: 8680, savings: 3720, confidence: 87, risk: 'low', breakEven: '2.1 months' },
    { action: 'Buy Azure RI', target: 'E-series VMs (8 instances)', cloud: 'Azure', currentCost: 5800, projectedCost: 3480, savings: 2320, confidence: 91, risk: 'low', breakEven: '5.8 months' },
    { action: 'Increase GCP CUD', target: 'N2 Machine Family (+20%)', cloud: 'GCP', currentCost: 4200, projectedCost: 2940, savings: 1260, confidence: 78, risk: 'medium', breakEven: '6.4 months' },
    { action: 'Downgrade SP commitment', target: 'SageMaker SP (over-committed)', cloud: 'AWS', currentCost: 5400, projectedCost: 3200, savings: 2200, confidence: 92, risk: 'low', breakEven: 'Immediate' },
  ],
  crossCloudComparison: [
    { workload: 'General Compute (4 vCPU, 16GB)', aws_onDemand: 198, aws_ri: 118, azure_onDemand: 186, azure_ri: 112, gcp_onDemand: 176, gcp_cud: 95, winner: 'GCP CUD' },
    { workload: 'GPU Training (A100)', aws_onDemand: 3672, aws_ri: 2203, azure_onDemand: 3420, azure_ri: 2052, gcp_onDemand: 3156, gcp_cud: 1893, winner: 'GCP CUD' },
    { workload: 'Database (8 vCPU, 64GB)', aws_onDemand: 890, aws_ri: 534, azure_onDemand: 820, azure_ri: 492, gcp_onDemand: 756, gcp_cud: 453, winner: 'GCP CUD' },
    { workload: 'Kubernetes Node (16 vCPU)', aws_onDemand: 540, aws_ri: 324, azure_onDemand: 498, azure_ri: 299, gcp_onDemand: 468, gcp_cud: 280, winner: 'GCP CUD' },
  ],
  riskAnalysis: {
    overCommitRisk: 12,
    underCommitCost: 89400,
    migrationRisk: 'Low',
    flexibilityScore: 72
  }
};

const CommitmentIntelligence = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Iter84ak — real backend; fall back to rich local demo.
    let cancelled = false;
    (async () => {
      if (!shouldShowData) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/finops/commitment-intelligence/recommendations`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!cancelled) { setData(COMMITMENT_DEMO_DATA); setLoading(false); }
      } catch (_e) {
        if (!cancelled) { setData(COMMITMENT_DEMO_DATA); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [shouldShowData]);

  if (!shouldShowData) {
    return <EmptyDataState title="Commitment Intelligence" description="Connect your cloud accounts to analyze Reserved Instances, Savings Plans, and Committed Use Discounts across all providers." />;
  }

  if (loading || !data) {
    return (
      <div className="p-6 space-y-4" data-testid="commitment-loading">
        {[1,2,3].map(i => <div key={i} className="h-32 bg-slate-100 dark:bg-slate-800/50 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  const { summary, currentCommitments, recommendations, crossCloudComparison, riskAnalysis } = data;
  const totalNewSavings = recommendations.reduce((a, r) => a + r.savings, 0);

  return (
    <div className="p-6 space-y-6" data-testid="commitment-intelligence">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Target className="w-7 h-7 text-amber-400" />
            Commitment Intelligence
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">Cross-cloud RI, Savings Plan & CUD optimization with risk analysis</p>
        </div>
        <Button variant="outline" size="sm" className="border-slate-300 dark:border-slate-600" onClick={() => toast.success('Analysis updated')}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-amber-50 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/40 dark:to-slate-900 border-amber-200 dark:border-amber-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 dark:text-slate-400">Coverage Gap</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.optimalCoverage - summary.currentCoverage}%</p>
                <p className="text-xs text-amber-400">Current: {summary.currentCoverage}% → Optimal: {summary.optimalCoverage}%</p>
              </div>
              <Target className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/40 dark:to-slate-900 border-emerald-200 dark:border-emerald-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 dark:text-slate-400">Annual Savings Gap</p>
                <p className="text-2xl font-bold text-emerald-400">${(summary.annualSavingsGap / 1000).toFixed(0)}K</p>
                <p className="text-xs text-slate-400 dark:text-slate-400">Achievable with optimal commitments</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/40 dark:to-slate-900 border-emerald-200 dark:border-blue-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 dark:text-slate-400">RI Utilization</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.riUtilization}%</p>
                <Progress value={summary.riUtilization} className="h-1.5 mt-2" />
              </div>
              <BarChart3 className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 bg-gradient-to-br from-red-50 to-white dark:from-red-900/40 dark:to-slate-900 border-red-200 dark:border-red-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 dark:text-slate-400">Over-Committed</p>
                <p className="text-2xl font-bold text-red-400">${(summary.overCommitted / 1000).toFixed(0)}K</p>
                <p className="text-xs text-slate-400 dark:text-slate-400">{summary.expiringNext30d} expiring in 30d</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <TabsTrigger value="overview">Active Commitments</TabsTrigger>
          <TabsTrigger value="recommendations">AI Recommendations</TabsTrigger>
          <TabsTrigger value="compare">Cross-Cloud Compare</TabsTrigger>
          <TabsTrigger value="risk">Risk Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-amber-400" />
                Current Commitments Portfolio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {currentCommitments.map((commitment) => (
                  <div key={commitment.id} className={`p-4 rounded-lg border ${
                    commitment.status === 'optimal' ? 'border-emerald-200 dark:border-emerald-700/50 bg-emerald-900/10' :
                    commitment.status === 'healthy' ? 'border-slate-200 dark:border-slate-700/50 bg-slate-800/30' :
                    commitment.status === 'warning' ? 'border-yellow-200 dark:border-yellow-700/50 bg-yellow-900/10' :
                    'border-red-200 dark:border-red-700/50 bg-red-900/10'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          commitment.status === 'optimal' ? 'bg-emerald-500' :
                          commitment.status === 'healthy' ? 'bg-emerald-500' :
                          commitment.status === 'warning' ? 'bg-yellow-500' :
                          'bg-red-500 animate-pulse'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{commitment.service}</p>
                          <p className="text-xs text-slate-400 dark:text-slate-400">{commitment.type} • {commitment.cloud} • {commitment.term}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-xs text-slate-400 dark:text-slate-400">Monthly</p>
                          <p className="text-sm font-bold text-slate-900 dark:text-white">${commitment.monthlyCommit.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-400 dark:text-slate-400">Utilization</p>
                          <p className={`text-sm font-bold ${
                            commitment.utilization > 85 ? 'text-emerald-400' :
                            commitment.utilization > 60 ? 'text-yellow-400' : 'text-red-400'
                          }`}>{commitment.utilization}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-400 dark:text-slate-400">Savings</p>
                          <p className="text-sm font-bold text-emerald-400">{commitment.savings}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-400 dark:text-slate-400">Expires</p>
                          <p className="text-sm text-slate-700 dark:text-slate-300">{commitment.expiry}</p>
                        </div>
                        <Badge className={`text-xs ${
                          commitment.status === 'optimal' ? 'bg-emerald-600' :
                          commitment.status === 'healthy' ? 'bg-blue-600' :
                          commitment.status === 'warning' ? 'bg-yellow-600' : 'bg-red-600'
                        }`}>{commitment.status}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommendations" className="mt-4 space-y-4">
          <Card className="bg-slate-900 bg-gradient-to-r from-emerald-900/30 to-slate-900 border-emerald-200 dark:border-emerald-700/30">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">AI-Recommended New Commitments</h3>
                  <p className="text-slate-400 dark:text-slate-400 text-sm">Based on 90-day usage patterns and workload stability analysis</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-400">${totalNewSavings.toLocaleString()}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-400">additional monthly savings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {recommendations.map((rec, i) => (
            <Card key={i} className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:border-slate-600 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">{rec.cloud}</Badge>
                      <Badge className={`text-xs ${rec.risk === 'low' ? 'bg-emerald-600' : 'bg-yellow-600'}`}>{rec.risk} risk</Badge>
                      <span className="text-xs text-slate-400 dark:text-slate-400">Confidence: {rec.confidence}%</span>
                    </div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">{rec.action}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">{rec.target}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-xs text-slate-400 dark:text-slate-400">Current: ${rec.currentCost.toLocaleString()}/mo</span>
                      <ArrowRight className="w-3 h-3 text-emerald-400" />
                      <span className="text-xs text-emerald-400">Projected: ${rec.projectedCost.toLocaleString()}/mo</span>
                      <span className="text-xs text-slate-400 dark:text-slate-400">Break-even: {rec.breakEven}</span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xl font-bold text-emerald-400">${rec.savings.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-400">/mo savings</p>
                    <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-xs">
                      Apply
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="compare" className="mt-4">
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-white flex items-center gap-2">
                <Scale className="w-5 h-5 text-blue-400" />
                Cross-Cloud Commitment Comparison (Monthly $/unit)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 text-slate-400 dark:text-slate-400 font-medium">Workload</th>
                      <th className="text-right py-3 text-orange-400 font-medium">AWS On-Demand</th>
                      <th className="text-right py-3 text-orange-300 font-medium">AWS RI</th>
                      <th className="text-right py-3 text-blue-400 font-medium">Azure On-Demand</th>
                      <th className="text-right py-3 text-blue-300 font-medium">Azure RI</th>
                      <th className="text-right py-3 text-emerald-400 font-medium">GCP On-Demand</th>
                      <th className="text-right py-3 text-emerald-300 font-medium">GCP CUD</th>
                      <th className="text-right py-3 text-amber-400 font-medium">Best Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crossCloudComparison.map((row, i) => (
                      <tr key={i} className="border-b border-slate-200 dark:border-slate-200 dark:border-slate-800/50 hover:bg-slate-800/20">
                        <td className="py-3 text-slate-900 dark:text-white font-medium">{row.workload}</td>
                        <td className="py-3 text-right text-slate-700 dark:text-slate-300">${row.aws_onDemand}</td>
                        <td className="py-3 text-right text-slate-700 dark:text-slate-300">${row.aws_ri}</td>
                        <td className="py-3 text-right text-slate-700 dark:text-slate-300">${row.azure_onDemand}</td>
                        <td className="py-3 text-right text-slate-700 dark:text-slate-300">${row.azure_ri}</td>
                        <td className="py-3 text-right text-slate-700 dark:text-slate-300">${row.gcp_onDemand}</td>
                        <td className="py-3 text-right text-emerald-400 font-semibold">${row.gcp_cud}</td>
                        <td className="py-3 text-right"><Badge className="bg-emerald-600 text-xs">{row.winner}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="risk" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-slate-900 dark:text-white">Risk Scorecard</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-700 dark:text-slate-300">Over-Commit Risk</span>
                  <div className="flex items-center gap-2">
                    <Progress value={riskAnalysis.overCommitRisk} className="w-20 h-2" />
                    <span className="text-sm font-bold text-emerald-400">{riskAnalysis.overCommitRisk}%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-700 dark:text-slate-300">Under-Commit Cost</span>
                  <span className="text-sm font-bold text-red-400">${(riskAnalysis.underCommitCost / 1000).toFixed(0)}K/year</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-700 dark:text-slate-300">Migration Risk</span>
                  <Badge className="bg-emerald-600">{riskAnalysis.migrationRisk}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-700 dark:text-slate-300">Flexibility Score</span>
                  <div className="flex items-center gap-2">
                    <Progress value={riskAnalysis.flexibilityScore} className="w-20 h-2" />
                    <span className="text-sm font-bold text-blue-400">{riskAnalysis.flexibilityScore}/100</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-slate-900 dark:text-white">Decision Matrix</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 rounded border border-emerald-200 dark:border-emerald-700/30 bg-emerald-900/10">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-300">Low Risk, High Savings</span>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-400">Stable workloads running 80%+ for 90+ days. Safe for 3-year RI.</p>
                </div>
                <div className="p-3 rounded border border-yellow-200 dark:border-yellow-700/30 bg-yellow-900/10">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-300">Medium Risk, Moderate Savings</span>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-400">Variable workloads. Use 1-year SP with flexibility. Monitor monthly.</p>
                </div>
                <div className="p-3 rounded border border-red-200 dark:border-red-700/30 bg-red-900/10">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-medium text-red-300">High Risk, Avoid Commitment</span>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-400">New or experimental workloads. Stay on-demand or use Spot.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CommitmentIntelligence;
